import { api } from '../lib/api';

export interface Reembolso {
  id: string;
  user_id: string;
  valor: number;
  motivo: string;
  status: 'PENDENTE' | 'APROVADO' | 'REJEITADO' | 'PROCESSADO';
  observacao_admin?: string;
  data_processamento?: string;
  // 🆕 Novos campos PIX
  cpf_chave_pix: string;
  transacao_credito_id?: string;
  comprovante_pix?: string; // JSON string
  created_at: string;
  updated_at: string;
}

export interface CriarReembolsoRequest {
  user_id: string;
  valor: number;
  motivo: string;
  cpf_chave_pix: string; // 🆕 CPF confirmado como chave PIX
}

export interface ComprovantePix {
  id_transacao_pix: string;
  data_transferencia: string;
  banco_origem: string;
  valor_transferido: number;
  observacoes_admin?: string;
}

export interface ProcessarReembolsoComProva {
  comprovante_pix: ComprovantePix;
  observacao_admin?: string;
}

export interface ReembolsosResponse {
  success: boolean;
  reembolsos: Reembolso[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
}

function isApiError(error: unknown): error is ApiError {
  return typeof error === 'object' && error !== null && 'response' in error;
}

function getErrorMessage(error: unknown, defaultMessage: string): string {
  if (isApiError(error)) {
    return error.response?.data?.message || error.message || defaultMessage;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return defaultMessage;
}

export class ReembolsoService {
  // POST /api/reembolsos - Criar nova solicitação de reembolso
  static async criarReembolso(dados: CriarReembolsoRequest): Promise<Reembolso> {
    try {
      
      
      const response = await api.post('/api/reembolsos', dados);

      
      return response.data.reembolso;
    } catch (error: unknown) {
      console.error('Erro ao criar reembolso:', error);
      throw new Error(getErrorMessage(error, 'Erro ao criar solicitação de reembolso'));
    }
  }

  // GET /api/reembolsos/usuario/:userId - Buscar reembolsos do usuário
  static async getReembolsosByUsuario(
    userId: string, 
    page: number = 1, 
    limit: number = 20
  ): Promise<ReembolsosResponse> {
    try {
      
      
      const response = await api.get(`/api/reembolsos/usuario/${userId}`, {
        params: { page, limit }
      });

      
      return response.data;
    } catch (error: unknown) {
      console.error('Erro ao buscar reembolsos do usuário:', error);
      throw new Error(getErrorMessage(error, 'Erro ao buscar reembolsos do usuário'));
    }
  }

  // GET /api/reembolsos - Buscar todos os reembolsos (admin)
  static async getAllReembolsos(
    page: number = 1, 
    limit: number = 20,
    filtros?: {
      status?: string;
      user_id?: string;
      data_inicio?: string;
      data_fim?: string;
    }
  ): Promise<ReembolsosResponse> {
    try {
      
      
      const response = await api.get('/api/reembolsos', {
        params: { page, limit, ...filtros }
      });

      

      return {
        success: response.data.success,
        reembolsos: response.data.reembolsos || [],
        total: response.data.pagination?.total || 0,
        page: response.data.pagination?.page || page,
        limit: response.data.pagination?.limit || limit,
        totalPages: response.data.pagination?.totalPages || 0
      };
    } catch (error: unknown) {
      console.error('Erro ao buscar reembolsos:', error);
      throw new Error(getErrorMessage(error, 'Erro ao buscar reembolsos'));
    }
  }

  // PUT /api/reembolsos/:id/status - Atualizar status do reembolso (admin)
  static async atualizarStatus(
    reembolsoId: string, 
    status: 'APROVADO' | 'REJEITADO' | 'PROCESSADO',
    observacaoAdmin?: string
  ): Promise<Reembolso> {
    try {
      
      
      const response = await api.put(`/api/reembolsos/${reembolsoId}/status`, {
        status,
        observacao_admin: observacaoAdmin
      });

      
      return response.data.reembolso;
    } catch (error: unknown) {
      console.error('Erro ao atualizar status do reembolso:', error);
      throw new Error(getErrorMessage(error, 'Erro ao atualizar status do reembolso'));
    }
  }

  // GET /api/reembolsos/:id - Buscar reembolso por ID
  static async getReembolsoById(reembolsoId: string): Promise<Reembolso> {
    try {
      
      
      const response = await api.get(`/api/reembolsos/${reembolsoId}`);

      
      return response.data.reembolso;
    } catch (error: unknown) {
      console.error('Erro ao buscar reembolso por ID:', error);
      throw new Error(getErrorMessage(error, 'Erro ao buscar reembolso'));
    }
  }

  // Formatar status para exibição
  static formatarStatus(status: string): string {
    const statusMap: { [key: string]: string } = {
      'PENDENTE': 'Pendente',
      'APROVADO': 'Aprovado',
      'REJEITADO': 'Rejeitado',
      'PROCESSADO': 'Processado'
    };

    return statusMap[status] || status;
  }

  // Obter cor baseada no status
  static obterCorStatus(status: string): string {
    const corMap: { [key: string]: string } = {
      'PENDENTE': 'bg-yellow-100 text-yellow-800',
      'APROVADO': 'bg-green-100 text-green-800',
      'REJEITADO': 'bg-red-100 text-red-800',
      'PROCESSADO': 'bg-blue-100 text-blue-800'
    };

    return corMap[status] || 'bg-gray-100 text-gray-800';
  }

  // Formatar data para exibição
  static formatarData(data: string): string {
    return new Date(data).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  // Formatar valor para exibição
  static formatarValor(valor: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  }

  /**
   * 🆕 Processar reembolso com comprovante PIX (Admin)
   */
  static async processarComComprovante(
    reembolsoId: string,
    dados: ProcessarReembolsoComProva
  ): Promise<Reembolso> {
    try {
      
      
      const response = await api.post(`/api/reembolsos/${reembolsoId}/processar-com-comprovante`, dados);

      
      return response.data.data;
    } catch (error: unknown) {
      console.error('Erro ao processar reembolso com comprovante:', error);
      throw new Error(getErrorMessage(error, 'Erro ao processar reembolso com comprovante PIX'));
    }
  }

  /**
   * 🆕 Validar CPF brasileiro
   */
  static validarCPF(cpf: string): boolean {
    const cpfLimpo = cpf.replace(/\D/g, '');
    
    if (cpfLimpo.length !== 11) return false;
    if (/^(\d)\1{10}$/.test(cpfLimpo)) return false; // Todos os dígitos iguais
    
    // Validação do dígito verificador
    let soma = 0;
    for (let i = 0; i < 9; i++) {
      soma += parseInt(cpfLimpo.charAt(i)) * (10 - i);
    }
    let digito1 = (soma * 10) % 11;
    if (digito1 === 10) digito1 = 0;
    
    if (parseInt(cpfLimpo.charAt(9)) !== digito1) return false;
    
    soma = 0;
    for (let i = 0; i < 10; i++) {
      soma += parseInt(cpfLimpo.charAt(i)) * (11 - i);
    }
    let digito2 = (soma * 10) % 11;
    if (digito2 === 10) digito2 = 0;
    
    return parseInt(cpfLimpo.charAt(10)) === digito2;
  }

  /**
   * 🆕 Formatar CPF para exibição
   */
  static formatarCPF(cpf: string): string {
    const cpfLimpo = cpf.replace(/\D/g, '');
    return cpfLimpo.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }

  /**
   * 🆕 Parsear comprovante PIX do JSON
   */
  static parseComprovantePix(comprovante: string | null): ComprovantePix | null {
    if (!comprovante) return null;
    try {
      return JSON.parse(comprovante);
    } catch {
      return null;
    }
  }
} 